import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import List from 'devextreme-react/list';
import { TabPanel, Item as TabItem } from 'devextreme-react/tab-panel';
import { Button } from 'devextreme-react/button';
import { TextBox } from 'devextreme-react/text-box';
import { confirm, custom } from 'devextreme/ui/dialog';
import { newToast } from '../../components';
import { useApiSqlEditor } from '../../api/api.sqlEditor';
import { SqlTab, SqlSchemaTable, SqlQueryResult, SqlRowEdit, SqlPreviewResult, SqlApplyEditsResult, SqlExecuteResult } from '../../types/types.sqlEditor';
import QueryTab from './QueryTab';
import StepUpPopup from './StepUpPopup';
import './SqlEditor.scss';

const TABLE_TOP = 200;
let tabSeq = 1;

export default () => {
  const { getSchema, query, applyEdits, preview, execute, getViewDefinition, executeDdl } = useApiSqlEditor();

  const [schema, setSchema] = useState<SqlSchemaTable[]>([]);
  const [search, setSearch] = useState('');
  const [tabs, setTabs] = useState<SqlTab[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [unlock, setUnlock] = useState<{ token: string; expiresAt: string } | null>(null);
  const [stepVisible, setStepVisible] = useState(false);

  // Ref para leer el token vigente dentro de callbacks sin recrearlos.
  const unlockRef = useRef(unlock);
  unlockRef.current = unlock;

  // El SQL de cada pestaña vive en un REF (no en el estado 'tabs'): así escribir/salir del foco
  // NO re-renderiza el TabPanel, evitando el remonte del editor y las fallas de selección de tab.
  const sqlStore = useRef<Record<string, string>>({});

  // AbortController por pestaña, para poder cancelar (detener) una ejecución en curso.
  // Al abortar la petición HTTP, ASP.NET dispara RequestAborted y SqlClient envía la señal de
  // cancelación a SQL Server (como el botón "detener" de SSMS), liberando los bloqueos.
  const abortRef = useRef<Record<string, AbortController>>({});

  const validToken = (): string | null => {
    const u = unlockRef.current;
    if (u && Date.parse(u.expiresAt) > Date.now()) return u.token;
    return null;
  };
  const isUnlocked = !!validToken();

  // ---- Carga del esquema ----
  useEffect(() => {
    getSchema().then((res) => {
      if (res.internalCode < 200 && Array.isArray(res.data)) setSchema(res.data as SqlSchemaTable[]);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Autocompletado CodeMirror: { tabla: [columnas] }
  const schemaForCM = useMemo(() => {
    const map: Record<string, string[]> = {};
    for (const t of schema) map[t.Name] = t.Columns.map((c) => c.Name);
    return map;
  }, [schema]);

  // Agrupadas por tipo (Vistas / Tablas) para la lista de la izquierda.
  // Inicialmente contraídas; al buscar se expanden para mostrar las coincidencias.
  const groupedTables = useMemo(() => {
    const term = search.trim().toLowerCase();
    const filtered = term ? schema.filter((t) => t.Name.toLowerCase().includes(term)) : schema;
    const byName = (a: SqlSchemaTable, b: SqlSchemaTable) => a.Name.localeCompare(b.Name);
    const views = filtered.filter((t) => t.IsView).sort(byName);
    const tables = filtered.filter((t) => !t.IsView).sort(byName);
    const groups: { key: string; items: SqlSchemaTable[]; collapsed: boolean }[] = [];
    if (views.length) groups.push({ key: `Vistas (${views.length})`, items: views, collapsed: !term });
    if (tables.length) groups.push({ key: `Tablas (${tables.length})`, items: tables, collapsed: !term });
    return groups;
  }, [schema, search]);

  // ---- Manejo de pestañas ----
  const addTab = (title: string, sqlText: string, autoRun: boolean) => {
    const key = `t${tabSeq++}`;
    const tab: SqlTab = { key, title, sql: sqlText };
    sqlStore.current[key] = sqlText;
    setTabs((prev) => {
      const next = [...prev, tab];
      setActiveIndex(next.length - 1);
      return next;
    });
    if (autoRun) setTimeout(() => runSelect(key, sqlText), 50);
  };

  const newQueryTab = () => addTab(`Consulta ${tabSeq}`, '', false);

  const openTableTab = (t: SqlSchemaTable) => {
    const sqlText = `SELECT TOP ${TABLE_TOP} * FROM [${t.Schema}].[${t.Name}]`;
    addTab(t.Name, sqlText, true);
  };

  // Abre una pestaña con el ALTER VIEW editable de la vista.
  const openViewEdit = async (t: SqlSchemaTable) => {
    const res = await getViewDefinition(t.Name);
    const def = (res.data as any)?.sql;
    if (res.internalCode < 200 && def) addTab(`${t.Name} (def)`, def, false);
  };

  // Al hacer clic en una tabla → SELECT directo. En una vista → preguntar: consultar o editar.
  const handleObjectClick = async (t: SqlSchemaTable) => {
    if (!t.IsView) {
      openTableTab(t);
      return;
    }
    const dialog = custom({
      title: `Vista: ${t.Name}`,
      messageHtml: 'Las vistas suelen abrirse para editar su definición. ¿Qué deseas hacer?',
      buttons: [
        { text: 'Consultar (SELECT)', onClick: () => 'select' },
        { text: 'Editar definición', type: 'default', onClick: () => 'edit' },
        { text: 'Cancelar', onClick: () => 'cancel' },
      ],
    });
    const r = await dialog.show();
    if (r === 'select') openTableTab(t);
    else if (r === 'edit') openViewEdit(t);
  };

  const closeTab = (key: string) => {
    delete sqlStore.current[key];
    setTabs((prev) => {
      const idx = prev.findIndex((x) => x.key === key);
      const next = prev.filter((x) => x.key !== key);
      setActiveIndex(Math.max(0, Math.min(idx, next.length - 1)));
      return next;
    });
  };

  const patchTab = (key: string, patch: Partial<SqlTab>) =>
    setTabs((prev) => prev.map((t) => (t.key === key ? { ...t, ...patch } : t)));

  // Persiste el SQL en el ref (sin re-render).
  const persistSql = useCallback((key: string, sqlText: string) => {
    sqlStore.current[key] = sqlText;
  }, []);

  const getTabSql = (key: string) => sqlStore.current[key] ?? tabs.find((t) => t.key === key)?.sql ?? '';

  // ---- Ejecución ----
  // Detiene la ejecución en curso de una pestaña (aborta la petición → cancela la consulta en BD).
  const cancelRun = (key: string) => {
    const c = abortRef.current[key];
    if (c) {
      c.abort();
      newToast('WARNING', 'Cancelando la ejecución…', 2);
    }
  };

  const runSelect = async (key: string, sqlOverride?: string) => {
    const sqlText = sqlOverride ?? getTabSql(key);
    if (!sqlText.trim()) {
      newToast('WARNING', 'La consulta está vacía.', 3);
      return;
    }
    sqlStore.current[key] = sqlText;
    const controller = new AbortController();
    abortRef.current[key] = controller;
    patchTab(key, { running: true, error: null });
    const res = await query(sqlText, undefined, controller.signal);
    if (abortRef.current[key] === controller) delete abortRef.current[key];
    if (res.internalCode === 499) {
      patchTab(key, { running: false, error: 'Ejecución cancelada.' });
      return;
    }
    if (res.internalCode < 200 && res.data) {
      patchTab(key, { running: false, result: res.data as SqlQueryResult, error: null });
    } else {
      patchTab(key, { running: false, result: null, error: res.message });
    }
  };

  const runWrite = async (key: string, sqlOverride?: string) => {
    const sqlText = sqlOverride ?? getTabSql(key);
    if (!sqlText.trim()) {
      newToast('WARNING', 'La sentencia está vacía.', 3);
      return;
    }
    sqlStore.current[key] = sqlText;
    const token = validToken();
    if (!token) {
      setStepVisible(true);
      return;
    }

    const controller = new AbortController();
    abortRef.current[key] = controller;
    patchTab(key, { running: true, error: null });
    try {
      // 1) Preview (dry-run con rollback).
      const prev = await preview(sqlText, token, controller.signal);
      if (prev.internalCode === 499) {
        patchTab(key, { error: 'Ejecución cancelada.' });
        return;
      }
      if (prev.internalCode >= 200 || !prev.data) return;
      const p = prev.data as SqlPreviewResult;

      // 2) Confirmación con el impacto real.
      const warn = !p.WithinLimit ? '<br/><b style="color:#d9534f">Supera el máximo permitido; se cancelará.</b>' : '';
      const ok = await confirm(
        `La operación <b>${p.StatementType}</b> afectará <b>${p.Affected}</b> fila(s).${warn}<br/><br/>¿Confirmas la ejecución?`,
        'Confirmar escritura'
      );
      if (!ok) return;

      // 3) Ejecución real.
      const exec = await execute(sqlText, undefined, token, controller.signal);
      if (exec.internalCode === 499) {
        patchTab(key, { error: 'Ejecución cancelada.' });
        return;
      }
      if (exec.internalCode < 200 && exec.data) {
        const r = exec.data as SqlExecuteResult;
        newToast('SUCCESS', `Ejecutado. ${r.Affected} fila(s) afectada(s).`, 4);
      }
    } finally {
      if (abortRef.current[key] === controller) delete abortRef.current[key];
      patchTab(key, { running: false });
    }
  };

  // Ejecuta un CREATE/ALTER VIEW (cambio de definición). Requiere step-up y confirmación.
  const runDdl = async (key: string, sqlOverride?: string) => {
    const sqlText = sqlOverride ?? getTabSql(key);
    if (!sqlText.trim()) {
      newToast('WARNING', 'La sentencia está vacía.', 3);
      return;
    }
    sqlStore.current[key] = sqlText;
    const token = validToken();
    if (!token) {
      setStepVisible(true);
      return;
    }
    const ok = await confirm('Se modificará la <b>definición de la vista</b> (CREATE/ALTER VIEW). ¿Continuar?', 'Confirmar cambio de esquema');
    if (!ok) return;

    const controller = new AbortController();
    abortRef.current[key] = controller;
    patchTab(key, { running: true, error: null });
    try {
      const res = await executeDdl(sqlText, token, controller.signal);
      if (res.internalCode === 499) {
        patchTab(key, { error: 'Ejecución cancelada.' });
        return;
      }
      if (res.internalCode < 200) newToast('SUCCESS', 'Definición de la vista actualizada.', 4);
    } finally {
      if (abortRef.current[key] === controller) delete abortRef.current[key];
      patchTab(key, { running: false });
    }
  };

  const saveEdits = async (table: string, edits: SqlRowEdit[], tabKey: string): Promise<boolean> => {
    const token = validToken();
    if (!token) {
      setStepVisible(true);
      return false;
    }
    const res = await applyEdits(table, edits, token);
    if (res.internalCode < 200 && res.data) {
      const r = res.data as SqlApplyEditsResult;
      if (r.Applied === edits.length) {
        newToast('SUCCESS', `${r.Applied} cambio(s) aplicado(s).`, 3);
        // Refresca la pestaña para reflejar ids nuevos / filas eliminadas.
        setTimeout(() => runSelect(tabKey), 0);
        return true;
      }
      const failed = (r.PerRow || []).filter((x) => !x.Ok);
      newToast('ERROR', `Aplicados ${r.Applied}/${edits.length}. ${failed.map((f) => f.Error).join(' | ')}`, 6);
      return false;
    }
    return false;
  };

  // ---- Render ----
  const renderTableItem = (t: SqlSchemaTable) => (
    <div className="sqle-table-item" title={`${t.Schema}.${t.Name}`}>
      <i className={`dx-icon-${t.IsView ? 'detailslayout' : 'tableproperties'}`} />
      <span>{t.Name}</span>
    </div>
  );

  const renderTabTitle = (tab: SqlTab) => (
    <div className="sqle-tab-title">
      <span>{tab.title}</span>
      <i
        className="dx-icon-close sqle-tab-close"
        onClick={(e) => {
          e.stopPropagation();
          closeTab(tab.key);
        }}
      />
    </div>
  );

  return (
    <div className="sqle-root">
      <div className="sqle-toolbar">
        <span className="sqle-title">Editor SQL</span>
        <Button icon="add" text="Nueva consulta" stylingMode="text" onClick={newQueryTab} />
        <div className="sqle-spacer" />
        <Button
          icon={isUnlocked ? 'unlock' : 'lock'}
          text={isUnlocked ? 'Escrituras habilitadas' : 'Desbloquear escrituras'}
          type={isUnlocked ? 'success' : 'normal'}
          stylingMode={isUnlocked ? 'outlined' : 'contained'}
          onClick={() => setStepVisible(true)}
        />
      </div>

      <div className="sqle-body">
        <div className="sqle-left">
          <TextBox
            value={search}
            onValueChanged={(e) => setSearch(e.value)}
            placeholder="Buscar tabla…"
            mode="search"
            stylingMode="outlined"
          />
          <List
            className="sqle-tables"
            dataSource={groupedTables}
            grouped
            collapsibleGroups
            keyExpr="Name"
            displayExpr="Name"
            searchEnabled={false}
            selectionMode="none"
            focusStateEnabled={false}
            itemRender={renderTableItem}
            onItemClick={(e) => {
              const item = e.itemData as unknown as SqlSchemaTable;
              if (item && item.Name) handleObjectClick(item);
            }}
            noDataText="Sin tablas"
          />
        </div>

        <div className="sqle-center">
          {tabs.length === 0 ? (
            <div className="sqle-empty-center">
              <p>Haz clic en una tabla para ver y editar sus registros, o crea una «Nueva consulta».</p>
            </div>
          ) : (
            <TabPanel
              selectedIndex={activeIndex}
              onSelectionChanged={(e) => {
                const idx = e.component.option('selectedIndex');
                if (typeof idx === 'number' && idx >= 0) setActiveIndex(idx);
              }}
              dataSource={tabs}
              itemTitleRender={renderTabTitle as any}
              swipeEnabled={false}
              animationEnabled={false}
              itemRender={(tab: SqlTab) => (
                <QueryTab
                  tab={tabs.find((t) => t.key === tab.key) || tab}
                  initialSql={sqlStore.current[tab.key] ?? tab.sql}
                  schemaForCM={schemaForCM}
                  unlockToken={validToken()}
                  onPersistSql={persistSql}
                  onRunSelect={(k, s) => runSelect(k, s)}
                  onRunWrite={(k, s) => runWrite(k, s)}
                  onRunDdl={(k, s) => runDdl(k, s)}
                  onSaveEdits={saveEdits}
                  onCancel={cancelRun}
                  requestStepUp={() => setStepVisible(true)}
                />
              )}
            />
          )}
        </div>
      </div>

      <StepUpPopup
        visible={stepVisible}
        onClose={() => setStepVisible(false)}
        onUnlocked={(token, expiresAt) => setUnlock({ token, expiresAt })}
      />
    </div>
  );
};
